import { useState } from 'react';
import type { SessionReminder } from '@/types';

interface Props {
  reminders: SessionReminder[];
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onAdd?: (text: string) => void;
  readOnly?: boolean;
  darkMode?: boolean;
}

export function Reminders({ reminders, onToggle, onDelete, onAdd, readOnly = false, darkMode = false }: Props) {
  const [newReminder, setNewReminder] = useState('');

  const handleAdd = () => {
    if (newReminder.trim() && onAdd) {
      onAdd(newReminder.trim());
      setNewReminder('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  const completedCount = reminders.filter(r => r.completed).length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 ${
          darkMode ? 'text-slate-400' : 'text-gray-500'
        }`}>
          <span>Reminders</span>
          {reminders.length > 0 && (
            <span className={darkMode ? 'text-slate-500 font-normal' : 'text-gray-400 font-normal'}>
              ({completedCount}/{reminders.length})
            </span>
          )}
        </h4>
      </div>

      {/* Add new reminder (only in setup mode) */}
      {!readOnly && onAdd && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newReminder}
            onChange={(e) => setNewReminder(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a reminder..."
            className={`flex-1 text-sm px-2.5 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 ${
              darkMode
                ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500'
                : 'border-gray-200 text-gray-900 placeholder-gray-400'
            }`}
          />
          <button
            onClick={handleAdd}
            disabled={!newReminder.trim()}
            className={`px-3 py-1.5 text-sm font-medium bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors ${
              darkMode
                ? 'disabled:bg-slate-600 disabled:text-slate-400 disabled:cursor-not-allowed'
                : 'disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed'
            }`}
          >
            +
          </button>
        </div>
      )}

      {/* Reminders list */}
      {reminders.length > 0 ? (
        <ul className="space-y-1">
          {reminders.map((reminder) => (
            <li
              key={reminder.id}
              className="flex items-start gap-2 text-sm py-1 group"
            >
              <button
                onClick={() => onToggle(reminder.id)}
                className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded border transition-colors ${
                  reminder.completed
                    ? 'bg-green-500 border-green-500 text-white'
                    : darkMode
                      ? 'border-slate-500 hover:border-green-500'
                      : 'border-gray-300 hover:border-green-500'
                }`}
              >
                {reminder.completed && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <span
                className={`flex-1 ${
                  reminder.completed
                    ? darkMode ? 'line-through text-slate-500' : 'line-through text-gray-400'
                    : darkMode ? 'text-slate-300' : 'text-gray-700'
                }`}
              >
                {reminder.text}
              </span>
              {!readOnly && (
                <button
                  onClick={() => onDelete(reminder.id)}
                  className={`opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity ${
                    darkMode ? 'text-slate-500' : 'text-gray-300'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className={`text-xs italic ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>No reminders</p>
      )}
    </div>
  );
}
